import React, { useState, useEffect } from 'react';
import { Card, Typography, Select, Switch, Row, Col, Tag, Rate, Avatar, Button, Modal, Form, Input, DatePicker, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import { teamIcon, playerIcon } from '../../components/community/mapIcons';

const { Title } = Typography;
const { Option } = Select;

// Fix para ícones default do Leaflet (usado só como fallback do L.Icon.Default)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Observa o zoom atual do mapa pra trocar o ícone do jogador por foto de perfil (zoom >= 14)
const ZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({ zoom: (e) => onZoomChange(e.target.getZoom()) });
  return null;
};

const OrganizerMap = () => {
  const [mapData, setMapData] = useState({ teams: [], players: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: 'both',
    skill_level: null,
    position: null,
    showTeams: true,
    showPlayers: true
  });
  const [mapBounds, setMapBounds] = useState(null);
  const [inviteModal, setInviteModal] = useState({ visible: false, player: null });
  const [openGames, setOpenGames] = useState([]);
  const [currentZoom, setCurrentZoom] = useState(9);

  const skillLevels = [
    { value: 'iniciante', label: 'Iniciante' },
    { value: 'recreativo', label: 'Recreativo' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' },
    { value: 'semi_pro', label: 'Semi-pro' }
  ];

  const positions = [
    { value: 'goleiro', label: 'Goleiro' },
    { value: 'zagueiro', label: 'Zagueiro' },
    { value: 'lateral', label: 'Lateral' },
    { value: 'volante', label: 'Volante' },
    { value: 'meia', label: 'Meia' },
    { value: 'atacante', label: 'Atacante' },
    { value: 'qualquer', label: 'Qualquer' }
  ];

  const fetchMapData = async (bounds) => {
    if (!bounds) return;
    
    setLoading(true);
    try {
      const params = {
        bounds: JSON.stringify(bounds),
        type: filters.type,
        ...(filters.skill_level && { skill_level: filters.skill_level }),
        ...(filters.position && { position: filters.position })
      };

      const response = await api.get('/community/map/data', { params });
      setMapData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados do mapa:', error);
    }
    setLoading(false);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const params = {
        type: filters.type,
        ...(filters.skill_level && { skill_level: filters.skill_level }),
        ...(filters.position && { position: filters.position })
      };

      const response = await api.get('/community/map/data', { params });
      setMapData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados iniciais:', error);
    }
    setLoading(false);
  };

  const handleMapMove = (map) => {
    const bounds = map.getBounds();
    const boundsObj = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    };
    setMapBounds(boundsObj);
    fetchMapData(boundsObj);
  };

  useEffect(() => {
    if (mapBounds) {
      fetchMapData(mapBounds);
    } else {
      fetchInitialData();
    }
  }, [filters]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const getSkillColor = (level) => {
    const colors = {
      iniciante: 'green',
      recreativo: 'blue',
      intermediario: 'orange',
      avancado: 'red',
      semi_pro: 'purple'
    };
    return colors[level] || 'default';
  };

  const loadOpenGames = async () => {
    try {
      // Buscar team_id do organizador
      const teamResponse = await api.get('/community/teams/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (teamResponse.data.id) {
        const gamesResponse = await api.get(`/community/games/team/${teamResponse.data.id}/open`);
        setOpenGames(gamesResponse.data);
      }
    } catch (error) {
      console.error('Erro ao carregar jogos:', error);
    }
  };

  const handleInvitePlayer = async (values) => {
    setLoading(true);
    try {
      await api.post('/community/invites/invite-player', {
        player_id: inviteModal.player.id,
        message: values.message,
        game_id: values.game_id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      message.success('Convite enviado!');
      setInviteModal({ visible: false, player: null });
      fetchInitialData();
    } catch (error) {
      message.error('Erro ao enviar convite');
    }
    setLoading(false);
  };
  
  const handleRespondApplication = async (applicationId, status) => {
    try {
      await api.put(`/community/invites/${applicationId}/respond`, { status }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      message.success(status === 'ACCEPTED' ? 'Candidatura aprovada!' : 'Candidatura recusada');
      fetchInitialData();
    } catch (error) {
      message.error('Erro ao responder candidatura');
    }
  };

  return (
    <div style={{ padding: '20px', height: '100vh' }}>
      <Card style={{ height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.history.back()}
            style={{ marginRight: 16 }}
          >
            Voltar
          </Button>
          <Title level={2} style={{ margin: 0 }}>Mapa da Comunidade</Title>
        </div>
        
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Select
              placeholder="Nível técnico"
              style={{ width: 150 }}
              allowClear
              value={filters.skill_level}
              onChange={(value) => setFilters({ ...filters, skill_level: value })}
            >
              {skillLevels.map(level => (
                <Option key={level.value} value={level.value}>{level.label}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="Posição"
              style={{ width: 150 }}
              allowClear
              value={filters.position}
              onChange={(value) => setFilters({ ...filters, position: value })}
            >
              {positions.map(pos => (
                <Option key={pos.value} value={pos.value}>{pos.label}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Switch
              checkedChildren="Times"
              unCheckedChildren="Times"
              checked={filters.showTeams}
              onChange={(checked) => setFilters({ 
                ...filters, 
                showTeams: checked,
                type: checked && filters.showPlayers ? 'both' : checked ? 'teams' : 'players'
              })}
            />
          </Col>
          <Col>
            <Switch
              checkedChildren="Jogadores"
              unCheckedChildren="Jogadores"
              checked={filters.showPlayers}
              onChange={(checked) => setFilters({ 
                ...filters, 
                showPlayers: checked,
                type: filters.showTeams && checked ? 'both' : checked ? 'players' : 'teams'
              })}
            />
          </Col>
        </Row>

        <div style={{ height: 'calc(100vh - 200px)', width: '100%' }}>
          <MapContainer
            center={[-23.2, -46.5]}
            zoom={9}
            style={{ height: '100%', width: '100%' }}
            whenReady={(map) => handleMapMove(map.target)}
            onMoveEnd={(e) => handleMapMove(e.target)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <ZoomWatcher onZoomChange={setCurrentZoom} />
            {filters.showTeams && mapData.teams.map(team => (
              <Marker
                key={`team-${team.id}`}
                position={[team.latitude, team.longitude]}
                icon={teamIcon({ openGames: team.open_games_week || 0 })}
              >
                <Popup>
                  <div style={{ minWidth: 250 }}>
                    <Title level={5}>{team.name}</Title>
                    {team.court_name && <p><strong>Quadra:</strong> {team.court_name}</p>}
                    <p><strong>Local:</strong> {team.neighborhood}, {team.city}</p>
                    <p><strong>Nível:</strong> <Tag color={getSkillColor(team.skill_level)}>{team.skill_level}</Tag></p>
                    {team.guest_price && <p><strong>Valor avulso:</strong> R$ {team.guest_price}</p>}
                    {team.game_schedule && team.game_schedule.length > 0 && (
                      <div>
                        <p><strong>Dias de jogos:</strong></p>
                        {team.game_schedule.map((schedule, index) => (
                          <p key={index} style={{ marginLeft: 10, marginBottom: 4 }}>{schedule}</p>
                        ))}
                      </div>
                    )}
                    {team.average_rating > 0 && (
                      <p><strong>Avaliação:</strong> <Rate disabled value={team.average_rating} /></p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {filters.showPlayers && mapData.players.map(player => (
              <Marker
                key={`player-${player.id}`}
                position={[player.latitude, player.longitude]}
                icon={playerIcon({
                  isGoalkeeper: player.preferred_position === 'goleiro',
                  photoUrl: player.profile_photo,
                  zoom: currentZoom,
                })}
              >
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Avatar src={player.profile_photo} style={{ marginRight: 8 }}>
                        {player.name.charAt(0)}
                      </Avatar>
                      <Title level={5} style={{ margin: 0 }}>{player.name}</Title>
                    </div>
                    <p><strong>Local:</strong> {player.neighborhood}, {player.city}</p>
                    <p><strong>Posição:</strong> {player.preferred_position}</p>
                    <p><strong>Nível:</strong> <Tag color={getSkillColor(player.skill_level)}>{player.skill_level}</Tag></p>
                    {player.preferred_position === 'goleiro' && player.game_price && (
                      <p><strong>Valor por jogo:</strong> R$ {player.game_price}</p>
                    )}
                    {player.average_rating > 0 && (
                      <p><strong>Avaliação:</strong> <Rate disabled value={player.average_rating} /></p>
                    )}
                    {player.last_invite_status && (
                      <p><strong>Último convite:</strong> 
                        <Tag color={
                          player.last_invite_status === 'PENDING' ? 'orange' :
                          player.last_invite_status === 'ACCEPTED' ? 'green' : 'red'
                        }>
                          {player.last_invite_status === 'PENDING' ? 'Pendente' :
                           player.last_invite_status === 'ACCEPTED' ? 'Aceito' : 'Recusado'}
                        </Tag>
                      </p>
                    )}
                    {player.has_pending_application ? (
                      <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => handleRespondApplication(player.application_id, 'ACCEPTED')}
                        >
                          Aprovar
                        </Button>
                        <Button 
                          size="small"
                          onClick={() => handleRespondApplication(player.application_id, 'REJECTED')}
                        >
                          Recusar
                        </Button>
                      </div>
                    ) : player.can_invite ? (
                      <Button 
                        type="primary" 
                        size="small" 
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          setInviteModal({ visible: true, player });
                          loadOpenGames();
                        }}
                      >
                        Convidar
                      </Button>
                    ) : (
                      <Button 
                        size="small" 
                        disabled
                        style={{ marginTop: 8 }}
                      >
                        {player.last_invite_status === 'PENDING' ? 'Convite Pendente' : 'Já Convidado'}
                      </Button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Modal de Convite */}
        <Modal
          title={`Convidar ${inviteModal.player?.name}`}
          open={inviteModal.visible}
          onCancel={() => setInviteModal({ visible: false, player: null })}
          footer={null}
        >
          <Form onFinish={handleInvitePlayer} layout="vertical">
            <Form.Item name="game_id" label="Jogo" rules={[{ required: true }]}>
              <Select placeholder="Selecione um jogo">
                {openGames.map(game => (
                  <Option key={game.id} value={game.id}>
                    {game.date} 
                    ({game.available_spots || (game.max_players - game.confirmed_players)} vagas)
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="message" label="Mensagem">
              <Input.TextArea rows={3} placeholder="Convite para jogar..." />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Enviar Convite
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default OrganizerMap;